import { useEffect, useId } from "react";
import type { Question } from "../../lib/survey-config";
import type { QuestionProps } from "./types";
import { QuestionHeader } from "./QuestionHeader";

type LongTextQuestion = Extract<Question, { type: "longText" }>;

function isValid(q: LongTextQuestion, v: string): boolean {
  const trimmed = v.trim();
  if (q.required === false) return true;
  if (q.minLength != null && trimmed.length < q.minLength) return false;
  if (q.maxLength != null && v.length > q.maxLength) return false;
  return trimmed.length > 0;
}

export function LongTextQuestion({
  question,
  value,
  onChange,
  onValid,
  autoFocus,
}: QuestionProps<LongTextQuestion, string>) {
  const id = useId();

  useEffect(() => {
    onValid(isValid(question, value));
  }, [question, value, onValid]);

  const optional = question.required === false;
  const trimmedLen = value.trim().length;
  const minLen = question.minLength ?? 0;
  const showMin = !optional && minLen > 0 && trimmedLen < minLen;

  return (
    <div className="space-y-4">
      <QuestionHeader
        title={question.title}
        subtitle={question.subtitle}
        required={!optional}
        htmlFor={id}
      />
      <textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={question.placeholder}
        maxLength={question.maxLength}
        autoFocus={autoFocus}
        rows={5}
        onKeyDown={(e) => {
          // Enter sendet weiter, Shift+Enter macht Zeilenumbruch.
          if (e.key === "Enter" && e.shiftKey) {
            e.stopPropagation();
          }
        }}
        className="w-full resize-y rounded-input border border-border bg-bg-elevated px-4 py-3 text-base leading-relaxed outline-none transition focus:border-accent"
      />
      <div className="flex justify-between text-xs text-fg-muted">
        <span>
          {optional
            ? "Optional"
            : showMin
              ? `Mindestens ${minLen} Zeichen (aktuell ${trimmedLen})`
              : "Sieht gut aus"}
        </span>
        {question.maxLength ? (
          <span>
            {value.length} / {question.maxLength}
          </span>
        ) : null}
      </div>
    </div>
  );
}
